import { DateTime } from 'luxon';
import { supabase } from '../supabase';

interface PromotionEntry {
  period: 'am' | 'pm';
  time: string;
  promoting_model: string;
  date: string;
}

interface PromotionAnalysisResult {
  [promoting_group: string]: {
    [promoted_group: string]: {
      [promoted_model: string]: PromotionEntry[];
    };
  };
}

interface PromotionAnalysisWithSubGroupsResult {
  byGroup: PromotionAnalysisResult;
  bySubGroup: {
    [promoting_sub_group: string]: {
      [promoted_sub_group: string]: {
        [promoted_model: string]: PromotionEntry[];
      };
    };
  };
}

class SupabasePromotionAnalysisService {

  /**
   * Analyzes promotion patterns across multiple daily formats
   * @param startDate Start date for analysis
   * @param endDate End date for analysis
   * @param modelIds Optional array of model IDs to filter by
   * @returns Structured promotion analysis data
   */
  async analyzePromotions(
    startDate: DateTime,
    endDate: DateTime,
    modelIds?: string[]
  ): Promise<PromotionAnalysisResult> {
    // Fetch all daily format slots in the date range using the view
    const { data: viewData, error } = await supabase
      .from('v_daily_model_lists_with_slots')
      .select('data')
      .gte('date', startDate.toISO()?.split('T')[0])
      .lte('date', endDate.toISO()?.split('T')[0]);

    if (error) {
      throw new Error(`Failed to fetch promotion data: ${error.message}`);
    }

    if (!viewData || viewData.length === 0) {
      return {};
    }

    // Flatten the slots from all lists
    const slots: any[] = [];
    for (const row of viewData) {
      const listData = row.data;
      if (listData?.slots) {
        for (const slot of listData.slots) {
          // Add the list metadata to each slot for easier processing
          slots.push({
            ...slot,
            list_id: listData.list_id,
            date: listData.date
          });
        }
      }
    }

    // Filter slots by criteria - only promo slots with assigned models, excluding any with metadata.type
    let filteredSlots = slots.filter(slot =>
      ['text', 'promo'].includes(slot.type) &&
      slot.model_id != null &&
      !slot.metadata?.type
    );

    if (filteredSlots.length === 0) {
      return {};
    }

    // Filter by model IDs if provided
    if (modelIds && modelIds.length > 0) {
      filteredSlots = filteredSlots.filter(slot =>
        slot.model_id && modelIds.includes(slot.model_id)
      );
    }

    // Get model-to-group and sub-group mappings
    const modelMappings = await this.getModelMappings();

    // Process slots to build promotion analysis
    const result: PromotionAnalysisResult = {};

    for (const slot of filteredSlots) {
      // Extract promoting model ID from list_id (format: "modelId_date")
      const promotingModelId = slot.list_id?.split('_')[0];
      if (!promotingModelId) continue;

      // Get group information
      const promotingGroup = modelMappings.groups[promotingModelId];
      const promotedGroup = modelMappings.groups[slot.model_id]; // The group of the promoted model
      const promotedModel = slot.model_id;

      if (!promotingGroup || !promotedGroup || !promotedModel) continue;

      // Initialize nested structure if needed
      if (!result[promotingGroup]) {
        result[promotingGroup] = {};
      }
      if (!result[promotingGroup][promotedGroup]) {
        result[promotingGroup][promotedGroup] = {};
      }
      if (!result[promotingGroup][promotedGroup][promotedModel]) {
        result[promotingGroup][promotedGroup][promotedModel] = [];
      }

      // Add promotion entry
      result[promotingGroup][promotedGroup][promotedModel].push({
        period: slot.slot_period as 'am' | 'pm',
        time: slot.time,
        promoting_model: promotingModelId,
        date: slot.date
      });
    }

    // Sort entries by time for each model
    for (const promotingGroup in result) {
      for (const promotedGroup in result[promotingGroup]) {
        for (const promotedModel in result[promotingGroup][promotedGroup]) {
          result[promotingGroup][promotedGroup][promotedModel].sort((a, b) => {
            // Sort by period first (am before pm), then by time
            if (a.period !== b.period) {
              return a.period === 'am' ? -1 : 1;
            }
            return a.time.localeCompare(b.time);
          });
        }
      }
    }

    return result;
  }

  /**
   * Analyzes promotion patterns with both group and sub-group breakdowns
   * @param startDate Start date for analysis
   * @param endDate End date for analysis
   * @param modelIds Optional array of model IDs to filter by
   * @returns Structured promotion analysis data with group and sub-group sections
   */
  async analyzePromotionsWithSubGroups(
    startDate: DateTime,
    endDate: DateTime,
    modelIds?: string[]
  ): Promise<PromotionAnalysisWithSubGroupsResult> {
    // Get the regular group-based analysis
    const byGroup = await this.analyzePromotions(startDate, endDate, modelIds);

    // Now get sub-group analysis
    const bySubGroup = await this.analyzePromotionsBySubGroups(startDate, endDate, modelIds);

    return {
      byGroup,
      bySubGroup
    };
  }

  /**
   * Analyzes promotion patterns by sub-groups (Referral, Top, Sub 100)
   */
  private async analyzePromotionsBySubGroups(
    startDate: DateTime,
    endDate: DateTime,
    modelIds?: string[]
  ): Promise<PromotionAnalysisWithSubGroupsResult['bySubGroup']> {
    // Fetch all daily format slots in the date range using the view
    const { data: viewData, error } = await supabase
      .from('v_daily_model_lists_with_slots')
      .select('data')
      .gte('date', startDate.toISO()?.split('T')[0])
      .lte('date', endDate.toISO()?.split('T')[0]);

    if (error) {
      throw new Error(`Failed to fetch promotion data: ${error.message}`);
    }

    if (!viewData || viewData.length === 0) {
      return {};
    }

    // Flatten the slots from all lists
    const slots: any[] = [];
    for (const row of viewData) {
      const listData = row.data;
      if (listData?.slots) {
        for (const slot of listData.slots) {
          slots.push({
            ...slot,
            list_id: listData.list_id,
            date: listData.date
          });
        }
      }
    }

    // Filter slots by criteria - only promo slots with assigned models that HAVE sub-group metadata
    let filteredSlots = slots.filter(slot =>
      slot.type === 'promo' &&
      slot.model_id != null &&
      slot.metadata?.type &&
      ['REFERRAL', 'TOP', 'SUB_100'].includes(slot.metadata.type)
    );

    if (filteredSlots.length === 0) {
      return {};
    }

    // Filter by model IDs if provided
    if (modelIds && modelIds.length > 0) {
      filteredSlots = filteredSlots.filter(slot =>
        slot.model_id && modelIds.includes(slot.model_id)
      );
    }

    // Get model groups for the promoting models
    const modelMappings = await this.getModelMappings();

    // Process slots to build sub-group promotion analysis
    const result: PromotionAnalysisWithSubGroupsResult['bySubGroup'] = {};

    for (const slot of filteredSlots) {
      // Extract promoting model ID from list_id (format: "modelId_date")
      const promotingModelId = slot.list_id?.split('_')[0];
      if (!promotingModelId) continue;

      // Get the promoting group from the model data
      const promotingGroup = modelMappings.groups[promotingModelId];
      if (!promotingGroup) continue;

      // The promoted sub-group comes from the slot metadata
      const promotedSubGroup = slot.metadata.type;
      const promotedModel = slot.model_id;

      // Use the promoting group as the promoting sub-group identifier
      const promotingSubGroup = promotingGroup;

      // Initialize nested structure if needed
      if (!result[promotingSubGroup]) {
        result[promotingSubGroup] = {};
      }
      if (!result[promotingSubGroup][promotedSubGroup]) {
        result[promotingSubGroup][promotedSubGroup] = {};
      }
      if (!result[promotingSubGroup][promotedSubGroup][promotedModel]) {
        result[promotingSubGroup][promotedSubGroup][promotedModel] = [];
      }

      // Add promotion entry
      result[promotingSubGroup][promotedSubGroup][promotedModel].push({
        period: slot.slot_period as 'am' | 'pm',
        time: slot.time,
        promoting_model: promotingModelId,
        date: slot.date
      });
    }

    // Sort entries by time for each model
    for (const promotingSubGroup in result) {
      for (const promotedSubGroup in result[promotingSubGroup]) {
        for (const promotedModel in result[promotingSubGroup][promotedSubGroup]) {
          result[promotingSubGroup][promotedSubGroup][promotedModel].sort((a, b) => {
            // Sort by period first (am before pm), then by time
            if (a.period !== b.period) {
              return a.period === 'am' ? -1 : 1;
            }
            return a.time.localeCompare(b.time);
          });
        }
      }
    }

    return result;
  }

  /**
   * Get model-to-group and sub-group mappings from the database
   */
  private async getModelMappings(): Promise<{
    groups: Record<string, string>;
    subGroups: Record<string, string[]>;
  }> {
    const { data: models, error } = await supabase
      .from('models')
      .select('id, scheduling_group, scheduling_sub_groups');

    if (error) {
      throw new Error(`Failed to fetch model mappings: ${error.message}`);
    }

    const groups: Record<string, string> = {};
    const subGroups: Record<string, string[]> = {};

    for (const model of models || []) {
      if (model.id && model.scheduling_group) {
        groups[model.id] = model.scheduling_group;
      }
      if (model.id && model.scheduling_sub_groups) {
        subGroups[model.id] = model.scheduling_sub_groups;
      }
    }

    return { groups, subGroups };
  }
}

const instance = new SupabasePromotionAnalysisService();

export default instance;
export type { PromotionEntry, PromotionAnalysisResult, PromotionAnalysisWithSubGroupsResult };